import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';

interface LogoProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showText?: boolean;
  variant?: 'horizontal' | 'stacked' | 'icon-only' | 'text-only';
  className?: string;
  /** Force a specific theme regardless of current theme (useful for landing page) */
  forceTheme?: 'light' | 'dark';
}

export const Logo: React.FC<LogoProps> = ({
  size = 'md',
  showText = true,
  variant = 'horizontal',
  className = '',
  forceTheme
}) => {
  const { isDark } = useTheme();

  // Determine which theme's logos to use
  // In dark mode or forced dark: use light logos (they show on dark backgrounds)
  // In light mode or forced light: use dark logos (they show on light backgrounds)
  const effectiveIsDark = forceTheme ? forceTheme === 'dark' : isDark;
  const logoSuffix = effectiveIsDark ? 'light' : 'dark';

  const sizes = {
    sm: { icon: 24, stacked: 48, textOnly: 20 },
    md: { icon: 32, stacked: 64, textOnly: 24 },
    lg: { icon: 48, stacked: 96, textOnly: 32 },
    xl: { icon: 64, stacked: 128, textOnly: 48 }
  };

  const { icon, stacked, textOnly } = sizes[size];

  // Logo file paths based on theme
  const logoIcon = `/assets/images/logo-${logoSuffix}.png`;
  const logoTextOnly = `/assets/images/logo-${logoSuffix}-text-only.png`;
  const logoWithText = `/assets/images/logo-${logoSuffix}-text.png`;

  // Text-only variant
  if (variant === 'text-only') {
    return (
      <div className={className}>
        <img
          src={logoTextOnly}
          alt="ParlayGround"
          style={{ height: textOnly }}
          className="object-contain"
        />
      </div>
    );
  }

  // Icon-only variant (no text)
  if (variant === 'icon-only' || !showText) {
    return (
      <div className={className}>
        <img
          src={logoIcon}
          alt="ParlayGround"
          style={{ width: icon, height: icon }}
          className="object-contain"
        />
      </div>
    );
  }

  // Stacked variant (logo above text)
  if (variant === 'stacked') {
    return (
      <div className={className}>
        <img
          src={logoWithText}
          alt="ParlayGround"
          style={{ height: stacked }}
          className="object-contain"
        />
      </div>
    );
  }

  // Horizontal variant (logo + text side by side)
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <img
        src={logoIcon}
        alt="ParlayGround"
        style={{ width: icon, height: icon }}
        className="object-contain"
      />
      <img
        src={logoTextOnly}
        alt="ParlayGround"
        style={{ height: icon * 0.6 }}
        className="object-contain"
      />
    </div>
  );
};

export default Logo;
